import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { useState } from 'react';

import { ActivityTable } from '~/components/tasks/detail/ActivityTable';
import { humanLogs, LOG_KIND, STATUS_COLOR } from '~/components/tasks/taskStyle';
import type { TaskDetail, TaskLog } from '~/data/generated/model';
import { BUTTON_SX } from '~/theme';

export function logChipStyle(log: TaskLog): { label: string; bg: string; fg: string } {
  if (log.label) {
    const color = STATUS_COLOR[log.label.color];
    return { label: log.label.name, bg: color.bg, fg: color.fg };
  }
  return LOG_KIND[log.kind];
}

const STATUS_LOG_KINDS = new Set<TaskLog['kind']>(['status_changed', 'task_complete', 'resolved']);

export function systemLogIds(task: TaskDetail): Set<string> {
  const human = new Set(humanLogs(task).map((log) => log.id));
  return new Set(
    task.logs
      .filter((log) => !human.has(log.id) || STATUS_LOG_KINDS.has(log.kind))
      .map((l) => l.id),
  );
}

type Props = {
  task: TaskDetail;
  onLogUpdate: () => void;
};

export function UpdateLog({ task, onLogUpdate }: Props) {
  const [hideSystem, setHideSystem] = useState(false);
  const systemIds = systemLogIds(task);

  const systemToggle =
    systemIds.size > 0 ? (
      <FormControlLabel
        sx={{ m: 0, color: 'text.secondary', whiteSpace: 'nowrap' }}
        slotProps={{ typography: { fontSize: 13 } }}
        control={
          <Checkbox
            size="small"
            checked={hideSystem}
            onChange={(e) => setHideSystem(e.target.checked)}
          />
        }
        label={`Hide system changes · ${systemIds.size}`}
      />
    ) : null;

  return (
    <Box
      component="section"
      aria-label="Activity"
      sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        <Box sx={{ flex: 1, minWidth: 240, fontSize: 14, color: '#555' }}>
          Updates the team logged on this task, with the files attached to each.
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onLogUpdate} sx={BUTTON_SX}>
          Log update
        </Button>
      </Box>

      <ActivityTable
        task={task}
        systemIds={systemIds}
        hideSystem={hideSystem}
        chipStyle={logChipStyle}
        toolbarStart={systemToggle}
      />
    </Box>
  );
}
